import { Card } from "@shared/components/Card";
import { Link } from "react-router-dom";

const LAST_UPDATED = "February 17, 2025";

export function LegalPage() {
  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="sticky top-0 z-10 border-b border-[var(--color-border-default)] bg-[var(--color-background)]">
        <div className="section-padding flex items-center gap-3">
          <Link
            to="/"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-[var(--color-foreground)] hover:bg-black/5"
            aria-label="Back"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <title>Back</title>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="font-mono text-[length:var(--font-size-headline-medium)] font-semibold text-[var(--color-foreground)]">
            Legal
          </h1>
        </div>
      </header>

      <main className="section-padding pb-8">
        <div className="mx-auto flex max-w-[var(--content-max-width)] flex-col gap-6">
          <Card constrainWidth className="flex flex-col gap-4 text-left">
            <h2 className="font-mono text-[length:var(--font-size-headline-small)] font-semibold text-[var(--color-foreground)]">
              Privacy Policy
            </h2>
            <p className="font-mono text-[length:var(--font-size-body-small)] text-lavender-700">
              Last updated: {LAST_UPDATED}
            </p>
            <p className="font-mono text-[length:var(--font-size-body-medium)]">
              At Fitties, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect
              your personal information.
            </p>
            <div className="flex flex-col gap-3">
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">
                Information We Collect
              </h3>
              <p className="whitespace-pre-line font-mono text-[length:var(--font-size-body-medium)]">
                {`• Profile information you provide
• Location data when using the app
• Usage information and preferences
• Messages and communication data`}
              </p>
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">
                How We Use Your Information
              </h3>
              <p className="whitespace-pre-line font-mono text-[length:var(--font-size-body-medium)]">
                {`• To provide and improve our services
• To facilitate matches and connections
• To ensure safety and security
• To communicate with you about our services`}
              </p>
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">Data Protection</h3>
              <p className="font-mono text-[length:var(--font-size-body-medium)]">
                We implement appropriate security measures to protect your personal information from unauthorized
                access, alteration, or disclosure.
              </p>
            </div>
          </Card>

          <Card constrainWidth className="flex flex-col gap-4 text-left">
            <h2 className="font-mono text-[length:var(--font-size-headline-small)] font-semibold text-[var(--color-foreground)]">
              Terms & Conditions
            </h2>
            <p className="font-mono text-[length:var(--font-size-body-small)] text-lavender-700">
              Last updated: {LAST_UPDATED}
            </p>
            <p className="font-mono text-[length:var(--font-size-body-medium)]">
              By using Fitties, you agree to these Terms & Conditions. Please read them carefully.
            </p>
            <div className="flex flex-col gap-3">
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">1. Service Terms</h3>
              <p className="whitespace-pre-line font-mono text-[length:var(--font-size-body-medium)]">
                {`• Must be 18 or older to use Fitties
• Provide accurate profile information
• Responsible for maintaining account security
• No unauthorized commercial activities`}
              </p>
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">2. User Conduct</h3>
              <p className="whitespace-pre-line font-mono text-[length:var(--font-size-body-medium)]">
                {`• Treat others with respect
• No harassment or inappropriate behavior
• No fake profiles or misrepresentation
• No illegal activities`}
              </p>
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">3. Payments</h3>
              <p className="whitespace-pre-line font-mono text-[length:var(--font-size-body-medium)]">
                {`• All purchases are final
• No refunds for paid event access once granted
• Prices subject to change
• You are responsible for all charges`}
              </p>
              <h3 className="font-mono text-[length:var(--font-size-body-large)] font-semibold">4. Termination</h3>
              <p className="font-mono text-[length:var(--font-size-body-medium)]">
                We reserve the right to terminate or suspend accounts that violate these terms or for any other reason
                at our discretion.
              </p>
            </div>
          </Card>

          <Card constrainWidth className="flex flex-col gap-4 text-left">
            <h2 className="font-mono text-[length:var(--font-size-headline-small)] font-semibold text-[var(--color-foreground)]">
              Message Data & Encryption
            </h2>
            <p className="font-mono text-[length:var(--font-size-body-small)] text-lavender-700">
              Last updated: {LAST_UPDATED}
            </p>
            <p className="font-mono text-[length:var(--font-size-body-medium)]">
              Your message data is encrypted for your privacy, however, in extreme cases we reserve the right to decrypt
              certain communications. In such cases, we do not pass this data onto any third party or authority, but
              they can be authenticated and delivered to one of the recipient users without consent of the other user.
            </p>
            <p className="font-mono text-[length:var(--font-size-body-medium)]">
              In this process, no member of the Fitties team is able to see the messages themselves.
            </p>
            <p className="font-mono text-[length:var(--font-size-body-medium)]">
              This feature is reserved for cases of extreme harassment or danger, and we at Fitties hope to never use
              it.
            </p>
          </Card>

          <Card constrainWidth className="flex flex-col gap-4 text-left">
            <h2 className="font-mono text-[length:var(--font-size-headline-small)] font-semibold text-[var(--color-foreground)]">
              Contact Us
            </h2>
            <p className="font-mono text-[length:var(--font-size-body-medium)]">
              If you have any questions about our Privacy Policy or Terms & Conditions, please contact us:
            </p>
            <a
              href="mailto:support@fitties.com"
              className="font-mono text-[length:var(--font-size-body-large)] font-medium text-primary hover:underline"
            >
              support@fitties.com
            </a>
            <p className="font-mono text-[length:var(--font-size-body-small)] text-lavender-700">
              Response time: Within 24 hours
            </p>
          </Card>
        </div>
      </main>
    </div>
  );
}
